import { ConfirmProvider, ToastProvider } from '@routeback/ui';
import { lazy, Suspense } from 'react';
import { HashRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { APP } from './app.config';
import { AuthGate } from './lib/auth';
import { PolicyNotice } from './lib/legal';
import { AppMark } from './lib/brand';
import { useApp } from './lib/app';
import { roleLabel } from './lib/roles';
import { SyncBadge } from './features/shell/SyncBadge';
import { TerminalPicker } from './features/shell/TerminalPicker';
import { StatusBanner } from './features/shell/StatusBanner';
import { PushSetup } from './features/shell/PushSetup';
import { HomeView } from './features/home/HomeView';
import { RequestsView } from './features/requests/RequestsView';
import { ScheduleView } from './features/schedule/ScheduleView';
import { MeView } from './features/me/MeView';
import { ShopView } from './features/shop/ShopView';
import { RailGroups } from './features/nav/RailGroups';
import { MANAGE_FEATURES } from './features/nav/features';
import { SignOffView } from './features/signoff/SignOffView';
import { useSignoffs } from './features/signoff/useSignoffs';
import { alwaysOpen } from './features/signoff/signoff';

const ReportProblemView = lazy(() => import('./features/maintenance/ReportProblemView').then((m) => ({ default: m.ReportProblemView })));
const MaintenanceDetailView = lazy(() => import('./features/maintenance/MaintenanceDetailView').then((m) => ({ default: m.MaintenanceDetailView })));
const TimeOffView = lazy(() => import('./features/timeoff/TimeOffView').then((m) => ({ default: m.TimeOffView })));
const TimeoffFormView = lazy(() => import('./features/timeoff/TimeoffFormView').then((m) => ({ default: m.TimeoffFormView })));
const TimeoffDetailView = lazy(() => import('./features/timeoff/TimeoffDetailView').then((m) => ({ default: m.TimeoffDetailView })));
const LineupView = lazy(() => import('./features/schedule/LineupView').then((m) => ({ default: m.LineupView })));
const MyDocumentsView = lazy(() => import('./features/docs/MyDocumentsView').then((m) => ({ default: m.MyDocumentsView })));
const UniformsView = lazy(() => import('./features/uniforms/UniformsView').then((m) => ({ default: m.UniformsView })));
const UniformRequestView = lazy(() => import('./features/uniforms/UniformRequestView').then((m) => ({ default: m.UniformRequestView })));
const VehicleDocsView = lazy(() => import('./features/vehicledocs/VehicleDocsView').then((m) => ({ default: m.VehicleDocsView })));
const AccidentView = lazy(() => import('./features/accident/AccidentView').then((m) => ({ default: m.AccidentView })));
const AccidentDetailView = lazy(() => import('./features/accident/AccidentDetailView').then((m) => ({ default: m.AccidentDetailView })));
const Op201View = lazy(() => import('./features/op201/Op201View').then((m) => ({ default: m.Op201View })));
const BulkPickupView = lazy(() => import('./features/bulk/BulkPickupView').then((m) => ({ default: m.BulkPickupView })));
const ResourcesView = lazy(() => import('./features/resources/ResourcesView').then((m) => ({ default: m.ResourcesView })));
const ResourcesAdminView = lazy(() => import('./features/resources/ResourcesAdminView').then((m) => ({ default: m.ResourcesAdminView })));
const MaintenanceDashboard = lazy(() => import('./features/maintdash/MaintenanceDashboard').then((m) => ({ default: m.MaintenanceDashboard })));
const WorkOrderView = lazy(() => import('./features/maintdash/WorkOrderView').then((m) => ({ default: m.WorkOrderView })));
const TimeoffAdminView = lazy(() => import('./features/timeoffadmin/TimeoffAdminView').then((m) => ({ default: m.TimeoffAdminView })));
const DriverDocsAdminView = lazy(() => import('./features/docsadmin/DriverDocsAdminView').then((m) => ({ default: m.DriverDocsAdminView })));
const ScheduleBuilderView = lazy(() => import('./features/schedadmin/ScheduleBuilderView').then((m) => ({ default: m.ScheduleBuilderView })));
const LineupAdminView = lazy(() => import('./features/lineupadmin/LineupAdminView').then((m) => ({ default: m.LineupAdminView })));
const UniformsAdminView = lazy(() => import('./features/uniformadmin/UniformsAdminView').then((m) => ({ default: m.UniformsAdminView })));
const VehicleDocsAdminView = lazy(() => import('./features/vehicledocsadmin/VehicleDocsAdminView').then((m) => ({ default: m.VehicleDocsAdminView })));
const WriteupsView = lazy(() => import('./features/writeups/WriteupsView').then((m) => ({ default: m.WriteupsView })));
const AccidentsAdminView = lazy(() => import('./features/formsadmin/FormsAdmin').then((m) => ({ default: m.AccidentsAdminView })));
const Op201AdminView = lazy(() => import('./features/formsadmin/FormsAdmin').then((m) => ({ default: m.Op201AdminView })));
const BulkAdminView = lazy(() => import('./features/formsadmin/FormsAdmin').then((m) => ({ default: m.BulkAdminView })));
const RecipientsView = lazy(() => import('./features/formsadmin/RecipientsView').then((m) => ({ default: m.RecipientsView })));
const HandbookAdminView = lazy(() => import('./features/signoffadmin/HandbookAdminView').then((m) => ({ default: m.HandbookAdminView })));
const CandidatesView = lazy(() => import('./features/candidates/CandidatesView').then((m) => ({ default: m.CandidatesView })));
const RoadTestView = lazy(() => import('./features/roadtest/RoadTestView').then((m) => ({ default: m.RoadTestView })));
const SettingsView = lazy(() => import('./features/settings/SettingsView').then((m) => ({ default: m.SettingsView })));
const ManageView = lazy(() => import('./features/manage/ManageView').then((m) => ({ default: m.ManageView })));
const PeopleView = lazy(() => import('./features/people/PeopleView').then((m) => ({ default: m.PeopleView })));
const TrucksView = lazy(() => import('./features/manage/TrucksView').then((m) => ({ default: m.TrucksView })));
const TerminalsView = lazy(() => import('./features/manage/TerminalsView').then((m) => ({ default: m.TerminalsView })));
const CompanySettingsView = lazy(() => import('./features/manage/CompanySettingsView').then((m) => ({ default: m.CompanySettingsView })));
const MessagesView = lazy(() => import('./features/messages/MessagesView').then((m) => ({ default: m.MessagesView })));
const MessageView = lazy(() => import('./features/messages/MessageView').then((m) => ({ default: m.MessageView })));
const MessagesAdminView = lazy(() => import('./features/messages/MessagesAdminView').then((m) => ({ default: m.MessagesAdminView })));
const BonusAdminView = lazy(() => import('./features/bonus/BonusAdminView').then((m) => ({ default: m.BonusAdminView })));
const ServiceReportView = lazy(() => import('./features/bonus/ServiceReportView').then((m) => ({ default: m.ServiceReportView })));
const MyBonusView = lazy(() => import('./features/bonus/MyBonusView').then((m) => ({ default: m.MyBonusView })));
const OnboardingWizard = lazy(() => import('./features/onboarding/OnboardingWizard').then((m) => ({ default: m.OnboardingWizard })));

/** Drivers: Home · Requests · Schedule · Me. Managers also get Manage. */
const TABS: { to: string; icon: string; label: string; manager?: boolean }[] = [
  { to: '/home', icon: '🏠', label: 'Home' },
  { to: '/requests', icon: '📨', label: 'Requests' },
  { to: '/schedule', icon: '📅', label: 'Schedule' },
  { to: '/manage', icon: '🧰', label: 'Manage', manager: true },
  { to: '/me', icon: '👤', label: 'Me' },
];

const ApplyPage = lazy(() => import('./features/candidates/ApplyPage').then((m) => ({ default: m.ApplyPage })));
const PublicLineupPage = lazy(() => import('./features/lineupadmin/PublicLineupPage').then((m) => ({ default: m.PublicLineupPage })));

export default function App() {
  // The public lineup page FedEx managers open (no login): app.routeback.app/lineup/{link}.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/lineup/')) {
    return <Suspense fallback={<div className="spinner" />}><PublicLineupPage /></Suspense>;
  }
  // The public Join Our Team page: app.routeback.app/apply/{company}.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/apply/')) {
    return <Suspense fallback={<div className="spinner" />}><ApplyPage /></Suspense>;
  }
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthGate>
          <HashRouter>
            <Shell />
          </HashRouter>
        </AuthGate>
      </ConfirmProvider>
    </ToastProvider>
  );
}

function Shell() {
  const app = useApp();
  const loc = useLocation();
  const isMechanic = app.role === 'mechanic';
  const tabs = isMechanic
    ? [{ to: '/home', icon: '🔧', label: 'Shop' }, { to: '/me', icon: '👤', label: 'Me' }]
    : TABS.filter((t) => !t.manager || app.isManager);
  // The owner sees the setup wizard until it's done or skipped.
  const needsSetup = app.role === 'owner' && app.settings['onboarding_done'] === false;
  if (needsSetup && loc.pathname !== '/setup') return <Navigate to="/setup" replace />;
  if (loc.pathname === '/setup') {
    return (
      <Suspense fallback={<div className="spinner" />}>
        {needsSetup ? <OnboardingWizard /> : <Navigate to="/home" replace />}
      </Suspense>
    );
  }
  // Drivers sign the handbook and consent before using the app (accident report and lineup stay open).
  const signoffs = useSignoffs();
  const locked = !!signoffs?.blocked && !alwaysOpen(loc.pathname);
  const manageLive = MANAGE_FEATURES.filter((f) => f.ready && f.to && (!f.admin || app.isAdmin) && (!f.flag || app.details.features?.[f.flag] === true));
  return (
    <div className="app">
      <nav className="rail" aria-label="Main">
        <h1><AppMark size={30} />{APP.name}</h1>
        {tabs.filter((t) => t.to !== '/manage').map((t) => (
          <NavLink key={t.to} to={t.to} className={({ isActive }) => (isActive ? 'active' : '')}><span>{t.icon}</span>{t.label}</NavLink>
        ))}
        {app.isManager && <RailGroups items={manageLive} />}
        <div className="rail-foot">
          {app.details.name}
          <br />
          {app.userName} · {roleLabel(app.role)}
        </div>
      </nav>
      <div className="app-body">
        <header className="app-header">
          <h1 className="header-title"><AppMark size={26} /><span>{app.details.name || APP.name}</span></h1>
          <TerminalPicker />
          <SyncBadge />
        </header>
        <StatusBanner />
        <PushSetup />
        <PolicyNotice />
        <main className="app-main">
          <Suspense fallback={<div className="spinner" />}>
            {locked ? <SignOffView /> : (
            <Routes>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={isMechanic ? <ShopView /> : <HomeView />} />
              <Route path="/requests" element={<RequestsView />} />
              <Route path="/requests/maintenance/:id" element={<MaintenanceDetailView />} />
              <Route path="/maintenance/new" element={<ReportProblemView />} />
              <Route path="/timeoff" element={<TimeOffView />} />
              <Route path="/timeoff/new" element={<TimeoffFormView />} />
              <Route path="/timeoff/:id/edit" element={<TimeoffFormView />} />
              <Route path="/requests/timeoff/:id" element={<TimeoffDetailView />} />
              <Route path="/schedule" element={<ScheduleView />} />
              <Route path="/lineup" element={<LineupView />} />
              <Route path="/docs" element={<MyDocumentsView />} />
              <Route path="/uniforms" element={<UniformsView />} />
              <Route path="/vehicle-docs" element={<VehicleDocsView />} />
              <Route path="/accident" element={<AccidentView />} />
              <Route path="/op201" element={<Op201View />} />
              <Route path="/signoff" element={<SignOffView />} />
              <Route path="/bulk" element={<BulkPickupView />} />
              <Route path="/requests/accident/:id" element={<AccidentDetailView />} />
              <Route path="/requests/uniforms/:id" element={<UniformRequestView />} />
              <Route path="/resources" element={<ResourcesView />} />
              <Route path="/messages" element={<MessagesView />} />
              <Route path="/bonus" element={<MyBonusView />} />
              <Route path="/manage/service" element={app.isManager ? <ServiceReportView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/bonuses" element={app.isManager ? <BonusAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/messages/:id" element={<MessageView />} />
              <Route path="/manage/messages" element={app.isManager ? <MessagesAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/resources" element={app.isManager ? <ResourcesAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/maintenance" element={app.isManager ? <MaintenanceDashboard /> : <Navigate to="/home" replace />} />
              <Route path="/manage/maintenance/:id" element={app.isManager ? <WorkOrderView /> : <Navigate to="/home" replace />} />
              <Route path="/shop/:id" element={app.isManager || isMechanic ? <WorkOrderView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/timeoff" element={app.isManager ? <TimeoffAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/driver-docs" element={app.isManager ? <DriverDocsAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/schedule" element={app.isManager ? <ScheduleBuilderView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/lineup" element={app.isManager ? <LineupAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/uniforms" element={app.isManager ? <UniformsAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/vehicle-docs" element={app.isManager ? <VehicleDocsAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/writeups" element={app.isManager ? <WriteupsView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/accidents" element={app.isManager ? <AccidentsAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/op201" element={app.isManager ? <Op201AdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/bulk" element={app.isManager ? <BulkAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/recipients" element={app.isAdmin ? <RecipientsView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/handbook" element={app.isManager ? <HandbookAdminView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/candidates" element={app.isManager ? <CandidatesView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/road-test" element={app.isManager && app.details.features?.['road_test'] === true ? <RoadTestView /> : <Navigate to="/home" replace />} />
              <Route path="/me" element={<MeView />} />
              <Route path="/manage" element={app.isManager ? <ManageView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/people" element={<PeopleView />} />
              <Route path="/manage/trucks" element={app.isManager ? <TrucksView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/terminals" element={app.isManager ? <TerminalsView /> : <Navigate to="/home" replace />} />
              <Route path="/manage/company" element={app.isAdmin ? <CompanySettingsView /> : <Navigate to="/home" replace />} />
              <Route path="/settings" element={<SettingsView />} />
              <Route path="/settings/:section" element={<SettingsView />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
            )}
          </Suspense>
        </main>
        <nav className="tabbar" aria-label="Tabs">
          {tabs.map((t) => (
            <NavLink key={t.to} to={t.to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {t.icon}
              <span>{t.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
